/**
 * The Play Entry form model: draft shape, the `POST /plays/v1` payload mapping
 * and the quarter/down auto-populate rule.
 *
 * Pure on purpose - no React, no fetch - so the smoke suite can drive the rules
 * directly and the page stays a rendering concern. The vocabulary lives in
 * `play-catalog.ts`; the field rules live in `play-validation.ts`.
 *
 * Post-submit next-play prediction (draft + phase) lives in
 * `predict-next-play.ts`.
 */
import { COMBINABLE_CONCEPT_FIELDS, CONCEPT_PARTS_FIELD } from '../../lib/concept-design';
import { PLAYCALL_FIELDS, PLAY_ENTRY_STAT_CARDS } from '../playbook/stat-catalog';

import { FIELD_BOUNDS, defaultPlayType, type PlayPhase } from './play-catalog';

export { PLAYCALL_FIELDS };

/**
 * The catalog-backed playcall, keyed by phase then by wire field. Kept per
 * phase so an O→D→O toggle never smuggles one phase's call onto the other
 * (the offense map is untouched while the defense map is edited) and so the
 * same wire name (`defensive_front`: theirs on offense, ours on defense) can
 * hold a different value on each phase.
 */
export type PlaycallDraft = Record<PlayPhase, Record<string, string>>;

export function createEmptyPlaycall(): PlaycallDraft {
  return { offense: {}, defense: {}, special_teams: {} };
}

/**
 * Ordered combined-concept pieces (Playbook option VALUES in called order),
 * per phase then by concept wire field (`offensive_concept` /
 * `defensive_concept`). Kept per phase for the same reason as the playcall.
 * The concept's own text (`playcall[phase][field]`) is always the generated
 * name for these pieces (`combinedConceptName`).
 */
export type ConceptPartsDraft = Record<PlayPhase, Record<string, readonly string[]>>;

export function createEmptyConceptParts(): ConceptPartsDraft {
  return { offense: {}, defense: {}, special_teams: {} };
}

/** Form state. Every value is a string: it comes from, and goes back to, inputs. */
export interface PlayDraft {
  quarter: string;
  gameClock: string;
  down: string;
  lineOfScrimmage: string;
  firstDownLine: string;
  gainLoss: string;
  hash: string;
  playbookNumber: string;
  playName: string;
  notes: string;
  tags: string;
  playType: string;
  playOutcome: string;
  specialtyOutcome: readonly string[];
  /**
   * Every catalog-backed stat (concept, formation, personnel, motion,
   * protection, front, coverage, blitz, the ST schemes), per phase, keyed by
   * wire field. See {@link PlaycallDraft}. The blitz booleans the Coaches
   * Highlight reads are derived server-side from `blitz_call`.
   */
  playcall: PlaycallDraft;
  /** Combined-concept pieces in called order. See {@link ConceptPartsDraft}. */
  conceptParts: ConceptPartsDraft;
  /** Direction the executed play went: `left` | `middle` | `right` | ''. */
  playDirection: string;
  kickDistance: string;
  returnYards: string;
  /**
   * One-click Red Zone tag. Posted as the `red-zone` tag (see `RED_ZONE_TAG`)
   * on top of whatever the operator typed into Tags. A boolean rather than
   * text so the chip is a toggle and the Tags field stays free-form.
   */
  isRedZone: boolean;
  /**
   * One-click Scramble tag — the quarterback ran on a called pass. Posted as
   * the `scramble` tag (see `SCRAMBLE_TAG`). Unlike Red Zone it never follows
   * the ball: it describes this snap only, so it resets after every submit.
   */
  isScramble: boolean;
  /**
   * One-tap "Offensive Penalty" chip — posted as the `offensive-penalty` tag
   * (see `OFFENSIVE_PENALTY_TAG`). Names the unit that FOULED, so stats
   * attribute the penalty to the offence whatever the logged possession. Like
   * Scramble it describes this snap only and resets after every submit; the
   * snap's result is still "No play" (which replays the down).
   */
  isOffensivePenalty: boolean;
  /** One-tap "Defensive Penalty" chip (`defensive-penalty` tag). See above. */
  isDefensivePenalty: boolean;
  /**
   * Tenant-defined stat fields (FR-STATS-01), keyed by `stat_key`. Strings like
   * every other draft value - `toCustomStatsPayload` in
   * `fields/custom-fields.tsx` converts them to typed JSON on submit.
   */
  customStats: Record<string, string>;
}

/** Fields that survive an O/D/ST toggle (AC-PLAY-005). */
export const COMMON_DRAFT_FIELDS: readonly (keyof PlayDraft)[] = [
  'quarter',
  'gameClock',
  'down',
  'lineOfScrimmage',
  'firstDownLine',
  'gainLoss',
  'hash',
  'playbookNumber',
  'playName',
  'notes',
  'tags',
  'playDirection',
  'isRedZone',
  'isScramble',
  'isOffensivePenalty',
  'isDefensivePenalty',
  // The playcall map is per phase internally, so it survives as a whole.
  'playcall',
  'conceptParts',
];

/** The tag the Red Zone chip applies. Kept in sync with the tags the archive filters on. */
export const RED_ZONE_TAG = 'red-zone';
/** The tag the Scramble chip applies (QB ran on a called pass). */
export const SCRAMBLE_TAG = 'scramble';
/**
 * Penalty tags — mirror of `OFFENSIVE_PENALTY_TAG` / `DEFENSIVE_PENALTY_TAG` in
 * `services/plays/src/constants.ts`. The tag names the unit that fouled.
 */
export const OFFENSIVE_PENALTY_TAG = 'offensive-penalty';
export const DEFENSIVE_PENALTY_TAG = 'defensive-penalty';

export interface QuickTag {
  tag: string;
  draftField: QuickTagField;
  label: string;
  /** Phases the chip is offered on (report: tags only on applicable plays). */
  phases: readonly PlayPhase[];
}

type QuickTagField = 'isRedZone' | 'isScramble' | 'isOffensivePenalty' | 'isDefensivePenalty';

/** Every one-tap quick-tag chip on Play Entry, for docs and filters. */
export const QUICK_TAGS: readonly QuickTag[] = [
  {
    tag: RED_ZONE_TAG,
    draftField: 'isRedZone',
    label: 'Red Zone',
    phases: ['offense', 'defense', 'special_teams'],
  },
  // A scramble is a scrimmage-snap event: never offered on Special Teams.
  {
    tag: SCRAMBLE_TAG,
    draftField: 'isScramble',
    label: 'Scramble',
    phases: ['offense', 'defense'],
  },
  {
    tag: OFFENSIVE_PENALTY_TAG,
    draftField: 'isOffensivePenalty',
    label: 'Offensive Penalty',
    phases: ['offense', 'defense', 'special_teams'],
  },
  {
    tag: DEFENSIVE_PENALTY_TAG,
    draftField: 'isDefensivePenalty',
    label: 'Defensive Penalty',
    phases: ['offense', 'defense', 'special_teams'],
  },
];

/** Human label for a quick-tag string (`offensive-penalty` → `Offensive Penalty`). */
export const QUICK_TAG_LABELS: Readonly<Record<string, string>> = Object.fromEntries(
  QUICK_TAGS.map((quick) => [quick.tag, quick.label])
);

/** The quick-tag chips offered on `phase`. */
export function quickTagsForPhase<T extends Pick<QuickTag, 'phases'>>(
  tags: readonly T[],
  phase: PlayPhase
): readonly T[] {
  return tags.filter((quick) => quick.phases.includes(phase));
}

/**
 * Whether a yard line is inside the red zone. Yard lines run 1-99 towards the
 * opponent's goal, so "the opponent's 20 or closer" is `100 - 20` and up.
 */
export function isRedZoneYardLine(yardLine: number | null): boolean {
  return (
    yardLine !== null && yardLine >= FIELD_BOUNDS.yardLineMax + 1 - FIELD_BOUNDS.redZoneYardLine
  );
}

/**
 * Typed tags plus the quick-tag chips (Red Zone, Scramble, Offensive / Defensive
 * Penalty), de-duplicated so an operator who also typed `red-zone` or
 * `scramble` by hand never posts it twice. Chip tags lead so they are visible
 * first in the archive's Tags cell. With `phase`, a chip that is not offered on
 * that phase (Scramble on Special Teams) is not posted even if it was left on.
 */
export function tagsForSubmit(
  draft: Pick<PlayDraft, 'tags' | QuickTagField>,
  phase?: PlayPhase
): string[] {
  const typed = parseTags(draft.tags);
  const chipTags: string[] = [];
  for (const quick of QUICK_TAGS) {
    if (draft[quick.draftField] !== true) continue;
    if (phase !== undefined && !quick.phases.includes(phase)) continue;
    chipTags.push(quick.tag);
  }
  const missing = chipTags.filter((chipTag) => !typed.some((tag) => tag.toLowerCase() === chipTag));
  return [...missing, ...typed];
}

export function createEmptyDraft(phase: PlayPhase = 'offense'): PlayDraft {
  return {
    quarter: '1',
    gameClock: '',
    down: phase === 'special_teams' ? '' : '1',
    lineOfScrimmage: '',
    firstDownLine: '',
    gainLoss: '',
    hash: '',
    playbookNumber: '',
    playName: '',
    notes: '',
    tags: '',
    playType: defaultPlayType(phase),
    playOutcome: 'na',
    specialtyOutcome: [],
    playcall: createEmptyPlaycall(),
    conceptParts: createEmptyConceptParts(),
    playDirection: '',
    kickDistance: '',
    returnYards: '',
    isRedZone: false,
    isScramble: false,
    isOffensivePenalty: false,
    isDefensivePenalty: false,
    customStats: {},
  };
}

/**
 * The "Situation" fields — where the ball is and when — that survive the
 * Play Entry **Clear All** button. Everything else on the draft describes the
 * play itself and is reset.
 */
export const SITUATION_DRAFT_FIELDS = [
  'quarter',
  'gameClock',
  'down',
  'lineOfScrimmage',
  'firstDownLine',
  'gainLoss',
  'hash',
] as const satisfies readonly (keyof PlayDraft)[];

/**
 * Clear All: a fresh draft for `phase` that keeps only the Situation fields
 * (see {@link SITUATION_DRAFT_FIELDS}). The playcall (every stat card on every
 * phase), play type (back to the phase default), result, specialty outcomes,
 * play direction, kick / return yards, playbook number / name, tags, notes,
 * custom stats and every quick-tag chip (Red Zone, Scramble, penalties) reset.
 *
 * Pure — like `predictNextPlay` — so the page applies it through the normal
 * draft patch (shared, offline and correction paths alike).
 */
export function clearPlayDraft(draft: PlayDraft, phase: PlayPhase): PlayDraft {
  const cleared = createEmptyDraft(phase);
  for (const field of SITUATION_DRAFT_FIELDS) cleared[field] = draft[field];
  return cleared;
}

export function parseTags(raw: string): string[] {
  return raw
    .split(',')
    .map((tag) => tag.trim())
    .filter((tag) => tag !== '');
}

/** A whole-number field value, or `null` when blank or unparseable. */
export function toNumberOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) ? parsed : null;
}

function textOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** The catalog half of the payload for `phase`: cards' values, others null. */
export function playcallPayload(
  playcall: PlaycallDraft,
  phase: PlayPhase
): Record<string, string | null> {
  const own = new Set(PLAY_ENTRY_STAT_CARDS[phase].map((card) => card.field));
  const values = playcall[phase] ?? {};
  return Object.fromEntries(
    PLAYCALL_FIELDS.map((field) => [field, own.has(field) ? textOrNull(values[field] ?? '') : null])
  );
}

/** Read one playcall value for the phase (`''` when unset). */
export function playcallValue(draft: PlayDraft, phase: PlayPhase, field: string): string {
  return draft.playcall[phase]?.[field] ?? '';
}

/** Immutable update of one playcall value on one phase. */
export function withPlaycallValue(
  draft: PlayDraft,
  phase: PlayPhase,
  field: string,
  value: string
): Pick<PlayDraft, 'playcall'> {
  return {
    playcall: { ...draft.playcall, [phase]: { ...draft.playcall[phase], [field]: value } },
  };
}

/** Read the ordered pieces of one concept field on one phase (`[]` when none). */
export function conceptPartsValue(
  draft: Pick<PlayDraft, 'conceptParts'>,
  phase: PlayPhase,
  field: string
): readonly string[] {
  return draft.conceptParts?.[phase]?.[field] ?? [];
}

/**
 * The `*_concept_parts` half of the payload: the ordered pieces when the call
 * combines 2+ concepts, otherwise `null` (a single piece is just the concept
 * value, exactly as before combined concepts existed). Special teams and
 * phases without the card send `null`.
 */
export function conceptPartsPayload(
  draft: Pick<PlayDraft, 'conceptParts'>,
  phase: PlayPhase
): Record<string, string[] | null> {
  const own = new Set(PLAY_ENTRY_STAT_CARDS[phase].map((card) => card.field));
  return Object.fromEntries(
    COMBINABLE_CONCEPT_FIELDS.map((field) => {
      const parts = own.has(field) ? conceptPartsValue(draft, phase, field) : [];
      return [CONCEPT_PARTS_FIELD[field], parts.length >= 2 ? [...parts] : null];
    })
  );
}

/**
 * Draft -> `POST /plays/v1` body (snake_case canonical field names).
 *
 * Fields belonging to another phase are sent as `null`, which the service
 * treats as absent: switching the toggle must not smuggle the previous phase's
 * formation onto this play.
 */
export function toPlayPayload(
  draft: PlayDraft,
  phase: PlayPhase,
  eventId: string,
  /**
   * Typed custom stat values for THIS phase, already converted by
   * `toCustomStatsPayload` (fields/custom-fields.tsx). Fields belonging to
   * another unit are absent by construction: a field the operator never saw
   * must not ride along on the play. Omitted entirely when empty, so a tenant
   * with no custom fields posts exactly the payload it always did.
   */
  customStats: Readonly<Record<string, unknown>> = {}
): Record<string, unknown> {
  const isSpecialTeams = phase === 'special_teams';
  return {
    event_id: eventId,
    client_timestamp: new Date().toISOString(),
    quarter: toNumberOrNull(draft.quarter),
    // Optional: a blank clock posts as null rather than an empty string.
    game_clock: textOrNull(draft.gameClock),
    possession: phase,
    play_type: draft.playType,
    play_outcome: draft.playOutcome === '' ? 'na' : draft.playOutcome,
    specialty_outcome: [...draft.specialtyOutcome],
    down: toNumberOrNull(draft.down),
    line_of_scrimmage: toNumberOrNull(draft.lineOfScrimmage),
    first_down_line: toNumberOrNull(draft.firstDownLine),
    gain_loss: toNumberOrNull(draft.gainLoss),
    hash: textOrNull(draft.hash),
    playbook_number: textOrNull(draft.playbookNumber),
    play_name: textOrNull(draft.playName),
    // Every catalog field: this phase's value where the phase has a card for
    // it, `null` otherwise, so the other phase's call never rides along.
    ...playcallPayload(draft.playcall, phase),
    // Combined concepts: ordered pieces (2+), else null. `designed_play_type`
    // is never sent: the server always derives it from the pieces' tags.
    ...conceptPartsPayload(draft, phase),
    // The blitz booleans Coaches Highlight reads are derived server-side from
    // `blitz_call`; posting null lets the service derive and, on a phase
    // change, clears a stale value left by the previous phase.
    opponent_blitz: null,
    defensive_blitz: null,
    play_direction: textOrNull(draft.playDirection),
    kick_distance: isSpecialTeams ? toNumberOrNull(draft.kickDistance) : null,
    return_yards: isSpecialTeams ? toNumberOrNull(draft.returnYards) : null,
    notes: textOrNull(draft.notes),
    tags: tagsForSubmit(draft, phase),
    ...(Object.keys(customStats).length === 0 ? {} : { custom_stats: { ...customStats } }),
  };
}
